import Modal from "Common/Components/Modal";
import { useFormik, FormikErrors } from "formik";
import * as Yup from "yup";
import React, { useState } from "react";
import { Edit, FileEdit } from "lucide-react";
import { useAppServices } from "hook/services";

interface NoteFormValues {
  title: string;
  description: string;
}

interface NoteEditModalProps {
  handleRefresh: () => void;
  editData: {
    _id?: string;
    title?: string;
    description?: string;
  } | null;
}

const NoteEditModal: React.FC<NoteEditModalProps> = ({
  handleRefresh,
  editData,
}) => {
  const AppService = useAppServices();
  const [show, setShow] = useState(false);

  const toggle = () => setShow(!show);
  // ✅ Submit handler
  const handleSubmit = async (values: NoteFormValues) => {
    const payload = {
      ...values,
      _id: editData?._id,
    };

    const { response } = await AppService.note.update({
      payload,
      toaster: true,
    });

    if (response) {
      toggle();
      handleRefresh();
    }
  };

  // ✅ Formik setup with types
  const formik = useFormik<NoteFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: editData?.title || "",
      description: editData?.description || "",
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required("Title is required")
        .min(2, "Title must be at least 2 characters")
        .max(100, "Title must be less than 100 characters"),
      description: Yup.string()
        .required("Description is required")
        .max(1000, "Description must be less than 1000 characters"),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <>
      <button
        onClick={toggle}
        className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors duration-150"
        title="Edit"
      >
        <Edit className="size-3.5 text-slate-500 dark:text-slate-400" />
      </button>
      <Modal
        show={show}
        onHide={toggle}
        id="noteModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-1/2 z-drawer -translate-x-1/2 -translate-y-1/2"
        dialogClassName="w-screen md:w-[40rem] bg-white dark:bg-slate-800 shadow-lg rounded-md"
      >
        {/* Header */}
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-slate-700">
          <Modal.Title className="text-lg font-semibold flex items-center gap-2 text-slate-800 dark:text-slate-200">
            <FileEdit className="w-5 h-5 text-blue-500" />
            Edit Note
          </Modal.Title>
        </Modal.Header>

        {/* Body */}
        <Modal.Body className="max-h-[calc(100vh-180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Title
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter note title"
                className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                  {formik.errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                Description
              </label>
              <textarea
                name="description"
                rows={4}
                placeholder="Write your note description here..."
                className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none transition-colors"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                  {formik.errors.description}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-md transition-all duration-200"
              >
                Update Note
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default NoteEditModal;
